import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabase'
import { requestTranslation } from '../../services/menuTranslations'
import { DEFAULT_ACCENT, PLACE_ID_FINDER, fetchPublicMenu, reviewLink, type MenuStyle, type PublicMenu } from '../../services/publicMenu'
import { MenuView } from '../menu/MenuView'
import './MenuStylePanel.css'

/** Hazır renkler: koyu zeminde de okunacak, beyaz yazıyı taşıyacak kadar koyu. */
const SWATCHES: { name: string; hex: string }[] = [
  { name: 'Turuncu', hex: DEFAULT_ACCENT },
  { name: 'Bordo', hex: '#9f1d35' },
  { name: 'Zeytin', hex: '#5b6b2f' },
  { name: 'Lacivert', hex: '#1f3a68' },
  { name: 'Kahve', hex: '#7a4b2a' },
  { name: 'Siyah', hex: '#141210' },
]

const THEMES: { id: NonNullable<MenuStyle['theme']>; label: string }[] = [
  { id: 'auto', label: 'Telefona uysun' },
  { id: 'light', label: 'Açık' },
  { id: 'dark', label: 'Koyu' },
]

/**
 * Ayarlar › QR Menü: menünün görünümü, yanında canlı önizleme.
 *
 * Önizleme müşterinin gördüğü menünün ta kendisi (MenuView), gerçek
 * ürünlerle; kaydedilmeden önce de değiştikçe güncelleniyor. Kaydedilen
 * tenants.menu_style, menü onu public_menu'dan okuyor.
 */
export function MenuStylePanel({ tenantId }: { tenantId: string }) {
  const [menu, setMenu] = useState<PublicMenu | null>(null)
  const [style, setStyle] = useState<MenuStyle>({})
  const [saved, setSaved] = useState<MenuStyle>({})
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchPublicMenu(tenantId)
      .then((m) => {
        if (!live || !m) return
        setMenu(m)
        setStyle(m.style)
        setSaved(m.style)
      })
      .catch(() => live && setNote('Menü okunamadı; önizleme gösterilemiyor.'))
    return () => {
      live = false
    }
  }, [tenantId])

  const accent = style.accent ?? DEFAULT_ACCENT
  const dirty = JSON.stringify(style) !== JSON.stringify(saved)
  const patch = (p: Partial<MenuStyle>) => {
    setNote(null)
    setStyle((s) => ({ ...s, ...p }))
  }

  const save = async () => {
    if (!supabase) return
    const review = style.reviewUrl?.trim() ? reviewLink(style.reviewUrl) : undefined
    if (style.reviewUrl?.trim() && !review) {
      setNote('Google yer kimliği (ChIJ… ile başlar) ya da bir Google bağlantısı (https://g.page/… gibi) girin.')
      return
    }
    setBusy(true)
    setNote(null)
    const clean: MenuStyle = {
      ...(style.accent && style.accent !== DEFAULT_ACCENT ? { accent: style.accent } : {}),
      ...(style.tagline?.trim() ? { tagline: style.tagline.trim() } : {}),
      ...(style.descriptions === false ? { descriptions: false } : {}),
      ...(style.theme && style.theme !== 'auto' ? { theme: style.theme } : {}),
      ...(review ? { reviewUrl: review } : {}),
    }
    const { error } = await supabase.from('tenants').update({ menu_style: clean }).eq('id', tenantId)
    setBusy(false)
    if (error) {
      setNote('Kaydedilemedi. Tekrar deneyin.')
      return
    }
    setSaved(clean)
    setStyle(clean)
    // Başlık altı yazısı değiştiyse çevirisi de yenilensin.
    if ((clean.tagline ?? '') !== (saved.tagline ?? '')) void requestTranslation(tenantId)
    setNote('Kaydedildi. Menü açık olan telefonlarda yenileyince görünür.')
  }

  return (
    <div className="mstyle">
      <div className="mstyle__controls">
        <div className="mstyle__field">
          <span className="mstyle__label">Vurgu rengi</span>
          <div className="mstyle__swatches">
            {SWATCHES.map((s) => (
              <button
                key={s.hex}
                type="button"
                className={`mstyle__swatch ${accent.toLowerCase() === s.hex ? 'is-on' : ''}`}
                style={{ background: s.hex }}
                onClick={() => patch({ accent: s.hex })}
                aria-label={s.name}
                title={s.name}
              />
            ))}
            <label className="mstyle__custom" title="Özel renk">
              <input
                id="menu-accent"
                type="color"
                value={accent}
                onChange={(e) => patch({ accent: e.target.value })}
              />
              Özel
            </label>
          </div>
        </div>

        <label className="mstyle__field">
          <span className="mstyle__label">Başlık altı yazısı</span>
          <input
            id="menu-tagline"
            className="mstyle__input"
            value={style.tagline ?? ''}
            onChange={(e) => patch({ tagline: e.target.value })}
            placeholder="Menü"
            maxLength={40}
          />
        </label>

        <label className="mstyle__check">
          <input
            id="menu-desc"
            type="checkbox"
            checked={style.descriptions !== false}
            onChange={(e) => patch({ descriptions: e.target.checked })}
          />
          Ürün açıklamalarını göster
        </label>

        <div className="mstyle__field">
          <span className="mstyle__label">Tema</span>
          <div className="mstyle__seg">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={(style.theme ?? 'auto') === t.id ? 'is-on' : ''}
                onClick={() => patch({ theme: t.id })}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <label className="mstyle__field">
          <span className="mstyle__label">Google yer kimliği</span>
          <input
            id="menu-review"
            className="mstyle__input"
            type="text"
            inputMode="url"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={style.reviewUrl ?? ''}
            onChange={(e) => patch({ reviewUrl: e.target.value })}
            placeholder="ChIJ…"
          />
          <span className="mstyle__hint">
            <a href={PLACE_ID_FINDER} target="_blank" rel="noreferrer">
              Google'ın yer kimliği sayfasında
            </a>{' '}
            işletmenizi aratın, çıkan "ChIJ…" ile başlayan kimliği kopyalayıp buraya yapıştırın. Müşteri menüde
            "Bizi değerlendirin" düğmesine basınca doğrudan yıldız verme penceresi açılır. İşletme Profili'nizdeki
            "Yorum iste" bağlantısını da yapıştırabilirsiniz. Boş bırakırsanız düğme görünmez.
          </span>
        </label>

        <div className="mstyle__actions">
          <button type="button" className="btn btn--primary" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          {dirty && (
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setStyle(saved)}>
              Vazgeç
            </button>
          )}
        </div>
        {note && <p className="mstyle__note">{note}</p>}
      </div>

      <div className="mstyle__preview" aria-label="Önizleme">
        <div className="mstyle__phone">
          {menu ? <MenuView menu={menu} style={style} favoritesKey={`onizleme.${tenantId}`} embedded /> : <p className="mstyle__note">Önizleme yükleniyor…</p>}
        </div>
      </div>
    </div>
  )
}
